import { Vector3 } from '@anov/3d-core'
import type { Object3D, SceneControl } from '@anov/3d-core'
import ObjectLayer from '../Base/ObjectLayer'
import type { AnimationOption } from './type'
import { EBuildingSplitType, EDirection } from './type'

type FloorComponent = {
  mesh: Object3D
  originalPosition: Vector3
  originalVisibility: boolean
}

type FloorGroup = {
  level: number
  isUnderground: boolean
  isRoof: boolean
  components: FloorComponent[]
}

class BuildingSplit extends ObjectLayer {
  private floorGroups: Map<string, FloorGroup> = new Map()
  private sortedFloorLevels: number[] = []
  private aboveGroundFloorCount = 0
  private undergroundFloorCount = 0
  private hasRoof = false
  private currentAnimation: { timer: number | null } = { timer: null }

  private _building: Object3D

  constructor(building: Object3D, sceneControl: SceneControl) {
    super(sceneControl)
    this._building = building
    this.init()
  }

  public get building() {
    return this._building
  }

  /**
   * 初始化建筑拆分器
   * @param building 建筑模型
   */
  public init(): void {
    this.floorGroups = new Map()
    this.sortedFloorLevels = []
    this.aboveGroundFloorCount = 0
    this.undergroundFloorCount = 0
    this.hasRoof = false

    // 解析所有楼层
    this.parseFloors()

    // 排序楼层编号从低到高
    this.sortedFloorLevels = Array.from(this.floorGroups.values())
      .map(group => group.level)
      .sort((a, b) => a - b)
  }

  /**
   * 解析建筑模型中的楼层
   */
  private parseFloors(): void {
    this.building.traverse((object) => {
      if (object.type === 'Mesh' || object.type === 'Group') {
        const name = object.name
        // 检查是否为屋顶
        if (name.startsWith('Roof')) {
          this.addToFloorGroup('Roof', {
            mesh: object,
            originalPosition: object.position.clone(),
            originalVisibility: object.visible,
          })
          this.hasRoof = true
          return
        }

        // 提取楼层前缀
        let prefix: string | null = null
        let level = 0
        let isUnderground = false

        if (name.startsWith('F') && name.length >= 3) {
          // 地上楼层
          const floorNum = parseInt(name.substring(1, 3))
          if (!isNaN(floorNum)) {
            prefix = `F${name.substring(1, 3)}`
            level = floorNum
            isUnderground = false
            this.aboveGroundFloorCount = Math.max(this.aboveGroundFloorCount, floorNum)
          }
        }
        else if (name.startsWith('B') && name.length >= 3) {
          // 地下楼层
          const floorNum = parseInt(name.substring(1, 3))
          if (!isNaN(floorNum)) {
            prefix = `B${name.substring(1, 3)}`
            level = -floorNum
            isUnderground = true
            this.undergroundFloorCount = Math.max(this.undergroundFloorCount, floorNum)
          }
        }

        if (prefix) {
          this.addToFloorGroup(prefix, {
            mesh: object,
            originalPosition: object.position.clone(),
            originalVisibility: object.visible,
          }, level, isUnderground)
        }
      }
    })
  }

  /**
   * 将组件添加到对应楼层分组
   */
  private addToFloorGroup(
    prefix: string,
    component: FloorComponent,
    level: number = this.aboveGroundFloorCount + 1,
    isUnderground = false,
  ): void {
    if (!this.floorGroups.has(prefix)) {
      this.floorGroups.set(prefix, {
        level,
        isUnderground,
        isRoof: prefix === 'Roof',
        components: [],
      })
    }

    this.floorGroups.get(prefix)?.components.push(component)
  }

  /**
   * 拆分楼层
   * @param options 拆分选项
   * @returns 成功或错误信息
   */
  public split(options): { success: boolean; message?: string } {
    const { floor, animation } = options
    const animOption: AnimationOption = animation || {
      type: EBuildingSplitType.Move,
      duration: 1000,
    }

    // 取消当前动画
    if (this.currentAnimation.timer !== null) {
      window.clearTimeout(this.currentAnimation.timer)
      this.currentAnimation.timer = null
    }

    // 处理 floor = 0 的情况：恢复完整状态
    if (floor === 0) {
      this.resetBuilding()
      return { success: true }
    }

    // 验证楼层参数
    if (floor > 0 && floor > this.aboveGroundFloorCount) {
      return {
        success: false,
        message: `地上楼层数超出范围，最大楼层为 ${this.aboveGroundFloorCount}`,
      }
    }

    if (floor < 0 && Math.abs(floor) > this.undergroundFloorCount) {
      return {
        success: false,
        message: `地下楼层数超出范围，最大地下楼层为 ${this.undergroundFloorCount}`,
      }
    }

    // 执行拆分操作
    switch (animOption.type) {
      case EBuildingSplitType.Hide:
        this.hideAboveFloors(floor)
        break
      case EBuildingSplitType.Move:
        this.moveAboveFloorsDirection(floor, animOption)
        break
      case EBuildingSplitType.Solo:
        this.showSoloFloor(floor)
        break
    }

    return { success: true }
  }

  /**
   * 隐藏指定楼层以上的楼层
   * @param targetFloor 目标楼层
   */
  private hideAboveFloors(targetFloor: number): void {
    for (const floorGroup of this.floorGroups.values()) {
      const floorLevel = floorGroup.level

      // 重置所有楼层组件位置
      for (const component of floorGroup.components)
        component.mesh.position.copy(component.originalPosition)

      // 设置可见性
      const shouldBeVisible = floorLevel <= targetFloor
      for (const component of floorGroup.components)
        component.mesh.visible = shouldBeVisible
    }
  }

  /**
   * 将指定楼层以上的楼层整体上移
   * @param targetFloor 目标楼层
   * @param animation 动画选项
   */
  private moveAboveFloorsUp(targetFloor: number, animation: AnimationOption): void {
    const distance = 10
    const duration = animation.duration

    // 重置所有楼层可见性
    for (const floorGroup of this.floorGroups.values()) {
      for (const component of floorGroup.components)
        component.mesh.visible = true
    }

    // 计算起始位置和目标位置
    const startPositions = new Map<Object3D, Vector3>()
    const targetPositions = new Map<Object3D, Vector3>()

    for (const floorGroup of this.floorGroups.values()) {
      const floorLevel = floorGroup.level

      for (const component of floorGroup.components) {
        startPositions.set(component.mesh, component.mesh.position.clone())

        if (floorLevel > targetFloor) {
          // 上方楼层需要上移
          const targetPos = component.originalPosition.clone()
          targetPos.y += distance
          targetPositions.set(component.mesh, targetPos)
        }
        else {
          // 其他楼层保持原位
          targetPositions.set(component.mesh, component.originalPosition.clone())
        }
      }
    }

    // 执行动画
    this.animatePositions(startPositions, targetPositions, duration)
  }

  /**
   * 将指定楼层以上的楼层向指定方向移动
   * @param targetFloor 目标楼层
   * @param animation 动画选项
   */
  private moveAboveFloorsDirection(targetFloor: number, animation: AnimationOption): void {
    const { direction = [10, 10, 0] } = animation.rule || {}
    const duration = animation.duration

    // 重置所有楼层可见性
    for (const floorGroup of this.floorGroups.values()) {
      for (const component of floorGroup.components)
        component.mesh.visible = true
    }

    // 计算起始位置和目标位置
    const startPositions = new Map<Object3D, Vector3>()
    const targetPositions = new Map<Object3D, Vector3>()

    for (const floorGroup of this.floorGroups.values()) {
      const floorLevel = floorGroup.level

      for (const component of floorGroup.components) {
        startPositions.set(component.mesh, component.mesh.position.clone())

        if (floorLevel > targetFloor) {
          // 上方楼层需要移动
          const targetPos = component.originalPosition.clone().add(new Vector3().fromArray(direction))
          targetPositions.set(component.mesh, targetPos)
        }
        else {
          // 其他楼层保持原位
          targetPositions.set(component.mesh, component.originalPosition.clone())
        }
      }
    }

    // 执行动画
    this.animatePositions(startPositions, targetPositions, duration)
  }

  /**
   * 计算方向向量
   * 规则：往右方(X)、前方(Z)是正、往左方(X)、后方(Z)是负数
   * @param direction 方向枚举
   * @param distance 移动距离
   * @returns Vector3 方向向量
   */
  private getDirectionVector(direction: EDirection, distance: number): Vector3 {
    const vector = new Vector3(0, 0, 0)
    const coefficient = 0.7071 // cos(45°) ≈ 0.7071
    switch (direction) {
      case EDirection.Right:
        vector.set(distance, 0, 0)
        break
      case EDirection.Left:
        vector.set(-distance, 0, 0)
        break
      case EDirection.Front:
        vector.set(0, 0, distance)
        break
      case EDirection.Back:
        vector.set(0, 0, -distance)
        break
      case EDirection.Right_Front:
        vector.set(distance * coefficient, distance * coefficient, distance * coefficient)
        break
      case EDirection.Left_Front:
        vector.set(-distance * coefficient, distance * coefficient, distance * coefficient)
        break
      case EDirection.Right_Back:
        vector.set(distance * coefficient, distance * coefficient, -distance * coefficient)
        break
      case EDirection.Left_Back:
        vector.set(-distance * coefficient, distance * coefficient, -distance * coefficient)
        break
      default:
        // 默认向右移动
        vector.set(distance, 0, 0)
    }

    return vector
  }

  /**
   * 只显示指定楼层
   * @param targetFloor 目标楼层
   */
  private showSoloFloor(targetFloor: number): void {
    for (const floorGroup of this.floorGroups.values()) {
      const floorLevel = floorGroup.level

      // 重置所有楼层组件位置
      for (const component of floorGroup.components)
        component.mesh.position.copy(component.originalPosition)

      // 设置可见性
      const shouldBeVisible = floorLevel === targetFloor
      for (const component of floorGroup.components)
        component.mesh.visible = shouldBeVisible
    }
  }

  /**
   * 执行位置动画
   * @param startPositions 起始位置
   * @param targetPositions 目标位置
   * @param duration 动画时长
   */
  private animatePositions(
    startPositions: Map<Object3D, Vector3>,
    targetPositions: Map<Object3D, Vector3>,
    duration: number,
  ): void {
    const startTime = performance.now()

    const animate = () => {
      const currentTime = performance.now()
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      targetPositions.forEach((targetPos, mesh) => {
        const startPos = startPositions.get(mesh)
        if (startPos) {
          // 线性插值计算当前位置
          mesh.position.lerpVectors(startPos, targetPos, progress)
        }
      })

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
      else {
        // 动画结束
        this.currentAnimation.timer = null
      }
    }

    animate()

    // 设置超时以确保动画完成
    this.currentAnimation.timer = window.setTimeout(() => {
      this.currentAnimation.timer = null
    }, duration + 100)
  }

  /**
   * 重置建筑到初始状态
   */
  private resetBuilding(): void {
    for (const floorGroup of this.floorGroups.values()) {
      for (const component of floorGroup.components) {
        // 恢复原始位置
        component.mesh.position.copy(component.originalPosition)

        // 恢复原始可见性
        component.mesh.visible = component.originalVisibility
      }
    }
  }

  /**
   * 获取楼层总数信息
   */
  public getFloorCounts(): { aboveGround: number; underground: number; hasRoof: boolean } {
    return {
      aboveGround: this.aboveGroundFloorCount,
      underground: this.undergroundFloorCount,
      hasRoof: this.hasRoof,
    }
  }
}

export { BuildingSplit, EBuildingSplitType }